import React from 'react';
import CSSModules from 'react-css-modules';
import FacebookShare from 'src/components/FacebookShare/FacebookShare';
import SupportersAvatar from 'src/components/SupportersAvatar/SupportersAvatar';
import constructSupportersList from 'src/components/SupportersAvatar/constructSupportersList';
import Meta from 'src/components/Page/Meta';
import Owner from 'src/components/Page/Owner';
import Progress from 'src/components/Page/Progress';
import styles from 'src/components/Page/PageHeader/PageHeader.scss';
import CoverImage from 'src/components/Page/CoverImage';
import DonateButton from 'src/components/Page/DonateButton';
import { PageState, ShareActionOptions } from 'src/redux/modules/page';
import { SupportersAvatarState } from 'src/redux/modules/supportersAvatar';
import { CategoryResource } from 'src/helpers/api/categories';
import RecurringPaymentsButtons from 'src/components/Page/RecurringPaymentsButtons/';
import CharityEndorsement from 'src/components/CharityEndorsed/CharityEndorsement';
import { CharityState } from 'src/redux/modules/charity/reducer';
import CopyLink from 'src/components/SocialShare/CopyLink';
import PageSupportsRecurringDonations from 'src/components/RecurringDonations/PageSupportsRecurringDonations';
import WhatIsCrowdfunding from 'src/components/WhatIsCrowdfunding/WhatIsCrowdfunding';
import ShareButton from 'src/components/ShareModal/ShareButton';
import { Slug } from 'src/redux/modules/ab';
import DonationReminder from 'src/components/Reminders';
import { Variation } from 'src/helpers/api/ab';
import ReminderModal from 'src/components/ReminderModal/ReminderModal';

export interface Props {
  categories: CategoryResource[];
  charity: CharityState;
  isActive: boolean;
  isClosed: boolean;
  canPageReceiveDonations: boolean;
  page: PageState;
  raised: number;
  totalCountSupporters: number;
  supportersAvatar: SupportersAvatarState;
  profileImage?: string;
  recaptcha: string;
  location?: string;
  goEdit: (section: string) => void;
  donate: () => void;
  loadSupportersAvatar: (details: { pageId: string; size: number }) => void;
  clearRecaptcha: () => void;
  contactOwner: (data: any) => void;
  supportsRecurringDonations: boolean;
  sharePageLink: (options: ShareActionOptions) => void;
  isEnrolledInShareButtonModal: boolean;
  handleShareButtonClick: () => void;
  displayShareButtonIcon: boolean;
  isDonationRemindLaterAvailable: boolean;
  donationReminderABVariation?: Variation;
  enrolAB: (slug: Slug) => void;
  handleReminderModalToggle: (isOpen: boolean) => void;
}

export interface State {
  isReminderModalOpen: boolean;
}

class PageHeader extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isReminderModalOpen: false,
    };
  }

  componentWillMount() {
    const {
      isClosed,
      loadSupportersAvatar,
      page: { id },
    } = this.props;
    const loadDetails = {
      pageId: id,
      size: 15,
    };

    if (isClosed) {
      loadSupportersAvatar(loadDetails);
    }
  }

  componentDidUpdate() {
    const {
      canPageReceiveDonations,
      isDonationRemindLaterAvailable,
      donationReminderABVariation,
      enrolAB,
    } = this.props;
    if (
      canPageReceiveDonations &&
      isDonationRemindLaterAvailable &&
      donationReminderABVariation === undefined
    ) {
      enrolAB('growth_remind_me_later');
    }
    return null;
  }

  donate = () => {
    const { donate, canPageReceiveDonations } = this.props;

    if (canPageReceiveDonations) {
      donate();
    }
  };

  renderDonate() {
    const {
      isClosed,
      canPageReceiveDonations,
      page: { name },
    } = this.props;

    if (isClosed) {
      return null;
    }

    return (
      <DonateButton
        donate={this.donate}
        isActive={canPageReceiveDonations}
        name={name}
        pageSection="page > top"
      />
    );
  }

  renderSupporters() {
    const {
      isClosed,
      supportersAvatar: { items },
    } = this.props;

    if (!isClosed && items.length < 1) {
      return null;
    }

    const supportersList = constructSupportersList(items);

    if (supportersList) {
      return <SupportersAvatar supportersList={supportersList} />;
    }

    return null;
  }

  renderShareButton() {
    const {
      page: { socialShareUrl },
      sharePageLink,
    } = this.props;

    const shareButtonLocation = 'page > page information';

    if (!this.props.isActive && !this.props.isClosed) {
      return (
        <div styleName="share-unavailable">
          <span>This page isn’t active yet.</span>
        </div>
      );
    }

    return (
      <div className="row jg-space-mtsm">
        <div className="col-sm-6">
          <FacebookShare
            buttonText="Share"
            location={shareButtonLocation}
            customStyle="jg-btn jg-btn--facebook"
            showCounter
            renderButtonOnly
          />
        </div>
        <div className="col-sm-6">
          <CopyLink
            copyText="Copy"
            copiedText="Copied"
            isDisabled={!this.props.isActive}
            pageSection={shareButtonLocation}
            socialShareUrl={socialShareUrl}
            sharePageLink={sharePageLink}
          />
        </div>
      </div>
    );
  }
  renderABTestShareButton() {
    return (
      <div className="jg-space-mtsm">
        <ShareButton
          displayShareButtonIcon={this.props.displayShareButtonIcon}
          handleShareClick={this.props.handleShareButtonClick}
        />
      </div>
    );
  }
  toggleReminderModal(isOpen: boolean) {
    this.setState({ isReminderModalOpen: isOpen });
    this.props.handleReminderModalToggle(isOpen);
  }

  renderBeMyFirstDonorProgress() {
    const {
      profileImage,
      page: { ownerName },
    } = this.props;

    const defaultMessage =
      'Be the first to help {ownerName} reach their {target} target';

    const additionalProps = {
      shouldShowProfileImage: true,
      profileImage,
      ownerName,
    };

    return this.renderProgess(defaultMessage, additionalProps);
  }

  renderDefaultProgress() {
    const defaultMessage = 'raised of {target} target by {supporters}';

    return this.renderProgess(defaultMessage);
  }

  renderProgess(defaultMessage: string, additionalProps?: any) {
    const {
      raised,
      page: { targetAmount, targetCurrency },
      totalCountSupporters,
    } = this.props;

    return (
      <Progress
        raised={raised}
        targetAmount={targetAmount}
        targetCurrency={targetCurrency}
        totalCountSupporters={totalCountSupporters}
        defaultMessage={defaultMessage}
        {...additionalProps}
      />
    );
  }

  render() {
    const {
      categories,
      charity,
      clearRecaptcha,
      contactOwner,
      goEdit,
      isClosed,
      location,
      page: {
        categoryId,
        finishedAt,
        id,
        initialImage,
        isOwnerRequested,
        name,
        ownerBio,
        ownerName,
        pitchFor,
        recurringDonationsPitch,
        status,
        targetAmount,
        targetCurrency,
        socialShareUrl,
      },
      profileImage,
      raised,
      recaptcha,
      supportsRecurringDonations,
      isEnrolledInShareButtonModal,
      donationReminderABVariation,
      isActive,
      totalCountSupporters,
      canPageReceiveDonations,
    } = this.props;

    return (
      <div styleName="container">
        <div styleName="cover-image-container">
          <CoverImage coverImage={initialImage} />
        </div>
        <div styleName="progress-donate-container">
          {totalCountSupporters === 0 && canPageReceiveDonations
            ? this.renderBeMyFirstDonorProgress()
            : this.renderDefaultProgress()}
          <div
            styleName="actions"
            className="jg-space-ptms jg-space-pbsm jg-space-phlg@xl jg-space-prml"
          >
            <div className="jg-display-b">
              {this.renderDonate()}
              {supportsRecurringDonations && (
                <div className="jg-space-mtsm jg-space-mbmd jg-text--center">
                  <RecurringPaymentsButtons
                    donate={this.donate}
                    name={name}
                    pageSection="page > top"
                  />
                </div>
              )}
            </div>
            {this.renderSupporters()}
            {isEnrolledInShareButtonModal
              ? this.renderABTestShareButton()
              : this.renderShareButton()}
          </div>
        </div>
        <div styleName="owner-meta-container">
          <div styleName="owner">
            <Owner
              clearRecaptcha={clearRecaptcha}
              contactOwner={contactOwner}
              id={id}
              isOwner={isOwnerRequested}
              name={name}
              ownerBio={ownerBio}
              ownerName={ownerName}
              pitchFor={pitchFor}
              profileImage={profileImage}
              recaptcha={recaptcha}
              targetAmount={targetAmount}
              targetCurrency={targetCurrency}
              isClosed={isClosed}
              raised={raised}
            />
          </div>

          <div styleName="meta">
            <Meta
              categories={categories}
              categoryId={categoryId}
              goEdit={goEdit}
              isOwner={isOwnerRequested}
              location={location}
              finishedAt={finishedAt}
              status={status}
              supportsRecurringDonations={supportsRecurringDonations}
            />
          </div>

          {charity && <CharityEndorsement charity={charity} />}
        </div>
        {supportsRecurringDonations && (
          <div
            className={`jg-space-mtsm jg-space-pbsm jg-text--center jg-bdb jg-bd--solid jg-bd--very-light-grey ${
              styles['recurring-donations']
            }`}
          >
            <RecurringPaymentsButtons
              donate={this.donate}
              name={name}
              pageSection="page > top"
            />
          </div>
        )}
        {supportsRecurringDonations && (
          <div className={`${styles['bottom-right']} jg-space-pmd`}>
            <PageSupportsRecurringDonations
              isOwnerRequested={isOwnerRequested}
              recurringDonationsPitch={recurringDonationsPitch}
            />
          </div>
        )}
        {donationReminderABVariation === 'B' && (
          <>
            <DonationReminder
              pageSection="page > header"
              handleClick={() => this.toggleReminderModal(true)}
            />
            <ReminderModal
              handleModalClose={() => this.toggleReminderModal(false)}
              showModal={this.state.isReminderModalOpen}
              pageSection="page > header"
              isActive={isActive}
              socialShareUrl={socialShareUrl}
              pitchFor={pitchFor}
              targetAmount={targetAmount}
              targetCurrency={targetCurrency}
              ownerName={ownerName}
            />
          </>
        )}
        {!supportsRecurringDonations &&
          donationReminderABVariation !== 'B' && (
            <div className={`${styles['bottom-right']}`}>
              <WhatIsCrowdfunding className="jg-space-ptms jg-space-psm" />
            </div>
          )}
      </div>
    );
  }
}

export default CSSModules(PageHeader, styles);



// WEBPACK FOOTER //
// ./src/components/Page/PageHeader/PageHeader.tsx